import { Inject, Injectable } from '@angular/core';
import { NavigationService } from '@core/services/navigation/navigation.service';
import { CommandAvailabilityService } from '@shared/services/command-availability.service';

import { IProductCommandContext } from '@shared/models/command';
import { BaseProductCommand } from '@shared/commands/base-product.command';
import { ArrestService } from '@core/services/arrests/arrest.service';
import { ProductTypes } from '@shared/models/products.model';
import { Observable } from 'rxjs';

/**
 * Команда для просмотра арестов и ограничений по продукту
 *
 * Эта команда предоставляет пользователю возможность просматривать информацию
 * об арестах и судебных ограничениях, наложенных на банковские продукты.
 * Поддерживает все типы продуктов включая счета, карты, депозиты и металлические счета.
 *
 * Основные функции:
 * - Проверка наличия арестов по номеру продукта
 * - Навигация на страницу детальной информации об арестах
 * - Отображение в интерфейсе только при наличии активных ограничений
 *
 * @example
 * ```typescript
 * // Использование для проверки арестов по счету
 * const context = { data: { number: '40817810123456789012' } };
 * const isAvailable = arrestCommand.isAvailable(context);
 * if (isAvailable) {
 *   await arrestCommand.execute(context);
 * }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class ArrestCommand extends BaseProductCommand {
  /** Уникальный идентификатор типа команды */
  readonly type = 'arrest';

  /** Заголовок команды в интерфейсе */
  readonly title = 'Аресты';

  /** Иконка команды, указывающая на предупреждение/ошибку */
  readonly icon = 'error';

  /** Типы продуктов, для которых доступна команда */
  readonly productType: ProductTypes[] = [
    ProductTypes.ACCOUNT,
    ProductTypes.CARD,
    ProductTypes.DEPOSIT,
    ProductTypes.SAVE_ACCOUNT,
    ProductTypes.METAL_ACCOUNT
  ];

  /** Флаг, указывающий что это не информационное действие */
  readonly isInfoAction = false;

  /** Настройки системы не требуются - доступность определяется наличием арестов */
  readonly settingsNames = undefined;

  /** Порядок отображения - первый приоритет из-за важности */
  readonly order = 1; // Первый в actions для всех типов продуктов

  constructor(
    commandAvailabilityService: CommandAvailabilityService,
    private navigationService: NavigationService,
    @Inject(ArrestService) private arrestService: ArrestService
  ) {
    super(commandAvailabilityService);
  }

  /**
   * Выполняет команду просмотра арестов
   *
   * Осуществляет навигацию на страницу с детальной информацией об арестах
   * и ограничениях по указанному номеру продукта.
   *
   * @param context - контекст команды с информацией о продукте
   */
  async execute(context: IProductCommandContext): Promise<void> {
    this.navigationService.push(`/other/arrest/${context.data.number}`);
  }

  /**
   * Проверяет наличие арестов по номеру продукта
   *
   * Команда доступна только в том случае, если по номеру продукта
   * найдены активные аресты или ограничения.
   *
   * @param context - контекст команды с информацией о продукте
   * @returns Observable<boolean> или boolean - true если есть аресты
   */
  protected checkAdditionalAvailability(context: IProductCommandContext): Observable<boolean> | boolean {
    return !!this.arrestService.getStayByAccNumber(context.data.number);
  }
}
